import SbrDrawer from '../sbr/SbrDrawer';
import { toLocalDateString } from '../../utils/helper';
import { Trans, useTranslation } from 'react-i18next';

const InboxIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>
);

const styles = `
  .sbr .pid-sec{margin-top:22px;padding-top:16px;border-top:1px solid var(--line)}
  .sbr .pid-sec:first-of-type{margin-top:0;padding-top:0;border-top:0}
  .sbr .pid-sec h4{font-size:11.5px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin:0 0 10px}
  .sbr .pid-rows{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:12px 18px}
  .sbr .pid-rows .full{grid-column:1 / -1}
  .sbr .pid-rows .l{font-size:10.5px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin-bottom:2px}
  .sbr .pid-rows .v{font-size:13.5px;font-weight:600;color:var(--ink);overflow-wrap:anywhere;line-height:1.45}
  .sbr .pid-rows .v.empty{color:var(--faint);font-weight:500}
  .sbr .pid-rows .v.pre{white-space:pre-wrap}
  .sbr .pid-src{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:800;letter-spacing:.03em;color:var(--muted);background:var(--surface,#f8fafc);border:1px solid var(--line);border-radius:999px;padding:3px 9px}
  .sbr .pid-src.link{color:var(--brand-ink,#1d4ed8);border-color:transparent;background:var(--brand-bg,#eff6ff)}
  .sbr .pid-dups{margin-top:12px;padding:10px 12px;border-radius:10px;background:var(--warn-bg);border:1px solid var(--warn-bd)}
  .sbr .pid-dups .t{font-size:12px;font-weight:800;color:var(--warn);margin-bottom:6px}
  .sbr .pid-dups label{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--ink);padding:4px 0;cursor:pointer}
  .sbr .pid-dups .by{font-size:11px;color:var(--muted);font-weight:700}
  .sbr .pid-foot{display:flex;gap:8px;flex-wrap:wrap}
  .sbr .pid-foot .danger{color:var(--bad);margin-left:auto}
`;

/** One labelled value. Empty optional fields still show, as a dash, so the
 *  reviewer can see what the customer left out rather than guessing. */
const Row = ({ label, value, full, pre }) => (
    <div className={full ? 'full' : ''}>
        <div className="l">{label}</div>
        <div className={`v${value ? (pre ? ' pre' : '') : ' empty'}`}>{value || '—'}</div>
    </div>
);

/**
 * Full detail of one pending customer submission.
 *
 * The card in the Pending requests list is a summary; this shows every field
 * the form collected, including the optional ones the customer skipped, plus
 * where the submission came from. Approve / Reject / Delete are repeated here
 * so the reviewer can decide without closing the panel — the mutations
 * themselves stay in pendingClients.jsx so there is one copy of that logic.
 */
const PendingIntakeDrawer = ({
    show, onHide, intake, choice = '', onChoice, onApprove, onReject, onDelete, busy,
}) => {
    const { t } = useTranslation();
    if (!intake) return null;

    const it = intake;
    const isCompany = it.clientType === 'COMPANY';
    const name = isCompany && it.companyName ? `${it.companyName} (${it.firstName} ${it.lastName})` : `${it.firstName} ${it.lastName}`;
    const dups = Array.isArray(it.possibleDuplicates) ? it.possibleDuplicates : [];
    const viaLink = !!(it.intakeLinkId || it.intakeLink);
    const source = viaLink ? `${t('pendingIntake.privateLink')}${it.intakeLink?.label ? ` · ${it.intakeLink.label}` : ''}` : t('pendingIntake.publicForm');

    return (
        <SbrDrawer
            show={show}
            onHide={onHide}
            secondary
            icon={<InboxIcon />}
            title={name}
            subtitle={t('clients.submittedDate', { date: toLocalDateString(it.createdAt, 'dd.MM.yyyy HH:mm') })}
            footer={(
                <div className="pid-foot">
                    <button type="button" className="btn-sbr btn-primary-sbr" disabled={busy} onClick={onApprove}>
                        {choice ? t('pendingIntake.approveUpdate') : t('pendingIntake.approveNew')}
                    </button>
                    <button type="button" className="btn-sbr btn-ghost-sbr" disabled={busy} onClick={onReject}>{t('pendingIntake.reject')}</button>
                    <button type="button" className="btn-sbr btn-ghost-sbr danger" disabled={busy} onClick={onDelete}>{t('common.delete')}</button>
                </div>
            )}
        >
            <style>{styles}</style>

            <div className="pid-sec">
                <h4>{t('pendingIntake.submission')}</h4>
                <div className="pid-rows">
                    <div>
                        <div className="l">{t('pendingIntake.source')}</div>
                        <div className="v"><span className={`pid-src${viaLink ? ' link' : ''}`}>{source}</span></div>
                    </div>
                    <Row label={t('pendingIntake.received')} value={toLocalDateString(it.createdAt, 'dd.MM.yyyy HH:mm')} />
                    <Row label={t('pendingIntake.type')} value={isCompany ? t('pendingIntake.company') : t('pendingIntake.person')} />
                    {it.ip && <Row label={t('pendingIntake.submittedFrom')} value={it.ip} />}
                </div>
            </div>

            <div className="pid-sec">
                <h4>{t('pendingIntake.contact')}</h4>
                <div className="pid-rows">
                    {isCompany && <Row label={t('pendingIntake.companyName')} value={it.companyName} full />}
                    <Row label={t('pendingIntake.firstName')} value={it.firstName} />
                    <Row label={t('pendingIntake.lastName')} value={it.lastName} />
                    <div>
                        <div className="l">{t('pendingIntake.phone')}</div>
                        <div className="v"><a href={`tel:${it.phone}`}>{it.phone}</a></div>
                    </div>
                    <div>
                        <div className="l">{t('pendingIntake.email')}</div>
                        <div className={`v${it.email ? '' : ' empty'}`}>
                            {it.email ? <a href={`mailto:${it.email}`}>{it.email}</a> : '—'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="pid-sec">
                <h4>{t('pendingIntake.address')}</h4>
                <div className="pid-rows">
                    <Row label={t('pendingIntake.street')} value={it.street} full />
                    <Row label={t('pendingIntake.apartment')} value={it.apartmentNo} />
                    <Row label={t('pendingIntake.floor')} value={it.floor} />
                    <Row label={t('pendingIntake.intercom')} value={it.intercomName} />
                    <Row label={t('pendingIntake.city')} value={it.city} />
                    <Row label={t('pendingIntake.county')} value={it.state} />
                    <Row label={t('pendingIntake.postcode')} value={it.zipCode} />
                    <Row label={t('pendingIntake.country')} value={it.country} />
                </div>
            </div>

            <div className="pid-sec">
                <h4>{t('pendingIntake.notesFromCustomer')}</h4>
                <div className="pid-rows">
                    <Row label={t('pendingIntake.accessSchedule')} value={it.notes} full pre />
                </div>
            </div>

            {dups.length > 0 && (
                <div className="pid-sec">
                    <h4>{t('pendingIntake.possibleDuplicates')}</h4>
                    <div className="pid-dups">
                        <div className="t">{t('pendingIntake.duplicateHint')}</div>
                        <label>
                            <input type="radio" name={`dm-${it.id}`} checked={choice === ''} onChange={() => onChoice('')} />
                            {t('pendingIntake.newClient')}
                        </label>
                        {dups.map((d) => (
                            <label key={d.id}>
                                <input type="radio" name={`dm-${it.id}`} checked={choice === d.id} onChange={() => onChoice(d.id)} />
                                <Trans i18nKey="pendingIntake.updateExisting" values={{ name: d.name, phone: d.phone }} components={{ b: <b /> }} />
                                {' '}<span className="by">· {d.matchedBy === 'phone' ? t('pendingIntake.samePhone') : d.matchedBy === 'email' ? t('pendingIntake.sameEmail') : t('pendingIntake.sameName')}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </SbrDrawer>
    );
};

export default PendingIntakeDrawer;
